import { useEffect, useMemo, useState } from 'react';
import { specialLabelsFor } from './specialDates';
import {
  API_BASE,
  sheetUrl,
  searchUrl,
  byIdUrl,
  todayCST,
  ALLERGY_PROFILES_SHEET,
  OUTGOING_EMAILS_SHEET,
  CHEF_EMAIL,
} from './config';
import { useTheme } from './theme';
import {
  parseChips,
  serializeChips,
  normalizePersonName,
  severityPillStyle,
  summaryString,
  nowIso,
  makeProfileId,
} from './allergies';

// Allergy lookup
// ----------------------------------------------------------------------------
// We pull two data sources:
//   1. AllergyProfiles — per-person rows keyed on (hostMemberNumber, name).
//      This is the new model: each named eater has their own row.
//   2. Members.allergy / allergyNote — legacy single-allergy-per-member.
//      We treat this as a *fallback* for members who haven't yet opened their
//      profile (which auto-migrates the legacy data on first save).
//
// For a booking row "Jane Smith (G)" tied to host #312, we strip the (G)/(c)
// tag, normalize, and look for an AllergyProfiles row with hostMemberNumber=312
// and name matching "jane smith". If found, that person's allergies are shown.
// If not found and the row name maps to the host themselves (no tag) we fall
// back to legacy Members.allergy. Otherwise no pill — same as today.

const loadAllergyData = async () => {
  const out = {
    profilesByHost: {},  // { '312': [ {name, normalizedName, allergies, note, severity, role}, ... ] }
    legacyByMember: {},  // { '312': { allergies, note } }
  };

  // Profiles
  try {
    const res = await fetch(sheetUrl(ALLERGY_PROFILES_SHEET), { cache: 'no-store' });
    if (res.ok) {
      const rows = await res.json();
      (rows || []).forEach((r) => {
        const host = String(r.hostMemberNumber || '').trim();
        if (!host) return;
        const allergies = parseChips(r.allergies);
        const note = String(r.note || '').trim();
        if (allergies.length === 0 && !note) return; // empty profile — skip
        const list = out.profilesByHost[host] || (out.profilesByHost[host] = []);
        list.push({
          name: String(r.name || '').trim(),
          normalizedName: normalizePersonName(r.name),
          role: (r.role || 'self').toString().toLowerCase(),
          allergies,
          note,
          severity: (r.severity || 'mild').toString().toLowerCase(),
        });
      });
    }
    // 404 / empty is fine — sheet not populated yet
  } catch (e) {
    console.warn('ChefDashboard: failed to load AllergyProfiles', e);
  }

  // Legacy Members data (fallback for un-migrated)
  try {
    const res = await fetch(sheetUrl('Members'), { cache: 'no-store' });
    if (res.ok) {
      const members = await res.json();
      (members || []).forEach((m) => {
        const key = String(m.memberNumber || '').trim();
        if (!key) return;
        const allergies = parseChips(m.allergy);
        const note = String(m.allergyNote || '').trim();
        if (allergies.length === 0 && !note) return;
        out.legacyByMember[key] = { allergies, note };
      });
    }
  } catch (e) {
    console.warn('ChefDashboard: failed to load Members legacy allergies', e);
  }

  return out;
};

// Resolve allergy info for one booking row.
// Returns { name, allergies: string[], note, severity } or null.
const resolveAllergyForRow = (row, allergyData) => {
  const host = String(row.memberNumber || '').trim();
  if (!host) return null;

  const profiles = allergyData.profilesByHost[host] || [];
  if (profiles.length > 0) {
    const target = normalizePersonName(row.name);
    if (!target) return null;
    // Exact normalized match first
    let hit = profiles.find((p) => p.normalizedName === target);
    // Otherwise: partial match (e.g. profile name "John" vs booking "John Smith")
    if (!hit && target) {
      hit =
        profiles.find((p) => p.normalizedName && target.includes(p.normalizedName)) ||
        profiles.find((p) => p.normalizedName && p.normalizedName.includes(target));
    }
    if (hit) {
      return {
        name: hit.name,
        allergies: hit.allergies,
        note: hit.note,
        severity: hit.severity,
      };
    }
    // Profiles exist for this host but this name wasn't found — return nothing
    // (don't fall through to legacy: per-person model is the truth once a host
    // has opted in).
    return null;
  }

  // Legacy fallback: only for member rows (not guests/children)
  const isGuest = /\(g\)/i.test(row.name || '');
  const isChild = /\(c\)/i.test(row.name || '');
  if (isGuest || isChild) return null;

  const legacy = allergyData.legacyByMember[host];
  if (!legacy) return null;
  return {
    name: row.name || '',
    allergies: legacy.allergies,
    note: legacy.note,
    severity: 'mild',
  };
};

// ===================================================
// MEAL CUTOFF (CHEF OVERRIDE)
// ---------------------------------------------------
// Chef can always modify anything — this is intentional
// and part of the contract. Do NOT replace with the
// shared cutoff function from specialDates.js.
// Deletions/voids are handled in your system via CD-
// rows and filtered out downstream.
// ===================================================
const isMealPastCutoff = () => false;

export default function ChefDashboard() {
  // Theme (shared across the app)
  const [theme, setTheme] = useTheme();

  // Mobile stacking for summary cards
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 760);
  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 760);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  // Load allergy data once (per-person profiles + legacy Members fallback).
  // Resolved per booking row at render time using resolveAllergyForRow().
  const [allergyData, setAllergyData] = useState({ profilesByHost: {}, legacyByMember: {} });
  const refreshAllergyData = async () => {
    const data = await loadAllergyData();
    setAllergyData(data);
  };
  useEffect(() => {
    refreshAllergyData();
  }, []);

  const S = stylesFor(theme);

  // Date selector
  const [selectedDate, setSelectedDate] = useState(todayCST());

  // Bookings
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);

  // Menu editor
  const [menu, setMenu] = useState({
    breakfast: '',
    lunch: '',
    dinner: '',
    chefs_note: '',
  });
  const [menuLoading, setMenuLoading] = useState(false);
  const [menuMsg, setMenuMsg] = useState('');

  // Fetch bookings for selected date
  const fetchBookingsForDate = async (date) => {
    setLoading(true);
    try {
      const res = await fetch(sheetUrl('Sheet1'), { cache: 'no-store' });
      const data = await res.json();

      const filtered = (data || []).filter(
        (row) => row.date === date && !(row.memberNumber || '').toString().startsWith('CD-')
      );
      setRows(filtered);
    } catch (err) {
      console.error('ChefDashboard: Error fetching bookings', err);
      setRows([]);
    } finally {
      setLoading(false);
    }
  };

  // Fetch menu for selected date
  const fetchMenuForDate = async (date) => {
    setMenuLoading(true);
    setMenuMsg('');
    try {
      const res = await fetch(searchUrl('Menu', { date }), { cache: 'no-store' });
      const data = await res.json();
      const row = data && data.length ? data[0] : null;

      if (row) {
        setMenu({
          breakfast: row.breakfast || '',
          lunch: row.lunch || '',
          dinner: row.dinner || '',
          chefs_note: row.chefs_note || '',
        });
        setMenuMsg('');
      } else {
        setMenu({ breakfast: '', lunch: '', dinner: '', chefs_note: '' });
        setMenuMsg('No menu set for this date yet.');
      }
    } catch (err) {
      console.error('ChefDashboard: Error fetching menu', err);
      setMenu({ breakfast: '', lunch: '', dinner: '', chefs_note: '' });
      setMenuMsg('Failed to load menu.');
    } finally {
      setMenuLoading(false);
    }
  };

  useEffect(() => {
    fetchBookingsForDate(selectedDate);
    fetchMenuForDate(selectedDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedDate]);

  // Save menu (update existing row by date or create new)
  const saveMenu = async () => {
    setMenuLoading(true);
    setMenuMsg('');
    try {
      const check = await fetch(searchUrl('Menu', { date: selectedDate }), { cache: 'no-store' });
      const existing = await check.json();

      const payload = {
        date: selectedDate,
        breakfast: menu.breakfast || '',
        lunch: menu.lunch || '',
        dinner: menu.dinner || '',
        chefs_note: menu.chefs_note || '',
      };

      if (existing && existing.length) {
        // Update by date (SheetDB column/value)
        const res = await fetch(
          `${API_BASE}/date/${encodeURIComponent(selectedDate)}?sheet=Menu`,
          {
            method: 'PATCH',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ data: payload }),
          }
        );
        if (!res.ok) throw new Error(await res.text());
      } else {
        // Create
        const res = await fetch(sheetUrl('Menu'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ data: payload }),
        });
        if (!res.ok) throw new Error(await res.text());
      }

      setMenuMsg('Menu saved.');
      await fetchMenuForDate(selectedDate);
    } catch (err) {
      console.error('ChefDashboard: Error saving menu', err);
      setMenuMsg('Failed to save menu.');
    } finally {
      setMenuLoading(false);
      setTimeout(() => setMenuMsg(''), 2500);
    }
  };

  // Toggle meal (Chef-side). Chef override always allows.
  const toggleMeal = async (row, meal) => {
    const currentYes = (row[meal] || '').toLowerCase() === 'yes';
    if (isMealPastCutoff(meal, row.date)) {
      alert(`${meal} cutoff passed for ${row.date}.`);
      return;
    }

    try {
      const res = await fetch(byIdUrl('Sheet1', row.id), {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: { [meal]: currentYes ? '' : 'Yes' } }),
      });
      if (!res.ok) throw new Error(await res.text());

      await fetchBookingsForDate(selectedDate);
    } catch (err) {
      console.error('ChefDashboard: toggleMeal failed', err);
      alert('Failed to update meal.');
    }
  };

  // Delete a booking row
  const deleteRow = async (row) => {
    if (!window.confirm(`Delete ${row.name} on ${row.date}?`)) return;
    try {
      const res = await fetch(byIdUrl('Sheet1', row.id), { method: 'DELETE' });
      if (!res.ok) throw new Error(await res.text());
      await fetchBookingsForDate(selectedDate);
    } catch (err) {
      console.error('ChefDashboard: deleteRow failed', err);
      alert('Failed to delete row.');
    }
  };

  // ===== Tomorrow's Attendance =====
  // Pulls all bookings for the next CST day, composes a chef-friendly HTML
  // email with totals + names + per-row allergies, and queues it in the
  // OutgoingEmails sheet. Apps Script drains that sheet and sends.
  const [tomorrowRows, setTomorrowRows] = useState([]);
  const [tomorrowLoading, setTomorrowLoading] = useState(false);
  const [tomorrowSending, setTomorrowSending] = useState(false);
  const [tomorrowMsg, setTomorrowMsg] = useState('');
  const [lastSentAt, setLastSentAt] = useState('');

  const tomorrowYmd = useMemo(() => {
    // todayCST returns YYYY-MM-DD in America/Chicago. Add 1 day.
    const today = todayCST();
    const [y, m, d] = today.split('-').map(Number);
    const dt = new Date(Date.UTC(y, m - 1, d));
    dt.setUTCDate(dt.getUTCDate() + 1);
    const yy = dt.getUTCFullYear();
    const mm = String(dt.getUTCMonth() + 1).padStart(2, '0');
    const dd = String(dt.getUTCDate()).padStart(2, '0');
    return `${yy}-${mm}-${dd}`;
  }, []);

  const fetchTomorrowRows = async () => {
    setTomorrowLoading(true);
    setTomorrowMsg('');
    try {
      const res = await fetch(searchUrl('Sheet1', { date: tomorrowYmd }), { cache: 'no-store' });
      const data = res.ok ? await res.json() : [];
      const filtered = (data || []).filter(
        (r) => !(r.memberNumber || '').toString().startsWith('CD-')
      );
      setTomorrowRows(filtered);
    } catch (e) {
      console.error('ChefDashboard: tomorrow fetch failed', e);
      setTomorrowRows([]);
      setTomorrowMsg('Failed to load tomorrow.');
    } finally {
      setTomorrowLoading(false);
    }
  };

  const fetchLastSent = async () => {
    try {
      const url = searchUrl(OUTGOING_EMAILS_SHEET, { kind: `attendance_${tomorrowYmd}` });
      const res = await fetch(url, { cache: 'no-store' });
      if (!res.ok) return;
      const data = await res.json();
      const sent = (data || [])
        .filter((r) => (r.status || '').toLowerCase() === 'sent')
        .sort((a, b) => (b.sentAt || '').localeCompare(a.sentAt || ''));
      if (sent[0]) setLastSentAt(sent[0].sentAt || '');
    } catch (e) {
      // silent — sheet may not exist yet
    }
  };

  useEffect(() => {
    fetchTomorrowRows();
    fetchLastSent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tomorrowYmd]);

  const tomorrowCounts = useMemo(() => {
    const init = {
      breakfast: { adults: 0, children: 0, guests: 0, total: 0 },
      lunch: { adults: 0, children: 0, guests: 0, total: 0 },
      dinner: { adults: 0, children: 0, guests: 0, total: 0 },
    };
    tomorrowRows.forEach((r) => {
      const name = (r.name || '').toString();
      const isChild = /\(c\)/i.test(name);
      const isGuest = /\(g\)/i.test(name);
      ['breakfast', 'lunch', 'dinner'].forEach((meal) => {
        if ((r[meal] || '').toLowerCase() === 'yes') {
          if (isChild) init[meal].children += 1;
          else {
            init[meal].adults += 1;
            if (isGuest) init[meal].guests += 1;
          }
          init[meal].total += 1;
        }
      });
    });
    return init;
  }, [tomorrowRows]);

  const tomorrowAllergies = useMemo(() => {
    // Per-row allergy info, keyed by booking row id, used in the email and preview
    const out = [];
    tomorrowRows.forEach((r) => {
      const a = resolveAllergyForRow(r, allergyData);
      if (!a) return;
      const summary = summaryString(serializeChips(a.allergies), a.note);
      if (!summary) return;
      out.push({
        rowId: r.id,
        name: r.name || a.name || '',
        memberNumber: r.memberNumber || '',
        severity: a.severity || 'mild',
        summary,
      });
    });
    return out;
  }, [tomorrowRows, allergyData]);

  // Build an HTML body for the email
  const buildTomorrowEmailHtml = () => {
    const esc = (s) =>
      String(s == null ? '' : s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;');

    const mealRow = (label, c) =>
      `<tr><td style="padding:6px 10px">${label}</td>` +
      `<td style="padding:6px 10px;text-align:right">${c.adults}</td>` +
      `<td style="padding:6px 10px;text-align:right">${c.children}</td>` +
      `<td style="padding:6px 10px;text-align:right">${c.guests}</td>` +
      `<td style="padding:6px 10px;text-align:right;font-weight:700">${c.total}</td></tr>`;

    const personRows = tomorrowRows
      .map((r) => {
        const meals = ['breakfast', 'lunch', 'dinner']
          .filter((m) => (r[m] || '').toLowerCase() === 'yes')
          .join(', ');
        if (!meals) return '';
        return `<tr><td style="padding:4px 10px">${esc(r.name)}</td>` +
          `<td style="padding:4px 10px;color:#64748b">${esc(r.memberNumber || '')}</td>` +
          `<td style="padding:4px 10px">${esc(meals)}</td></tr>`;
      })
      .filter(Boolean)
      .join('');

    const allergyRows = tomorrowAllergies
      .map((a) =>
        `<tr><td style="padding:4px 10px;font-weight:${a.severity === 'severe' ? 800 : 600};color:${a.severity === 'severe' ? '#7f1d1d' : '#7c2d12'}">` +
        `${a.severity === 'severe' ? '🚨 SEVERE' : '⚠'} ${esc(a.name)} (#${esc(a.memberNumber)})</td>` +
        `<td style="padding:4px 10px">${esc(a.summary)}</td></tr>`
      )
      .join('');

    return `<!doctype html><html><body style="font-family:Arial;color:#0f172a;max-width:760px;margin:0 auto;padding:14px">
  <h2 style="margin:0 0 4px 0">Wauhillau — Attendance for ${esc(tomorrowYmd)}</h2>
  <div style="color:#64748b;font-size:13px;margin-bottom:14px">Generated ${esc(new Date().toLocaleString())}</div>

  <h3 style="margin-bottom:6px">Counts</h3>
  <table style="border-collapse:collapse;width:100%;font-size:13px;border:1px solid #e5e7eb">
    <thead style="background:#f8fafc">
      <tr><th style="padding:6px 10px;text-align:left">Meal</th>
      <th style="padding:6px 10px;text-align:right">Adults</th>
      <th style="padding:6px 10px;text-align:right">Children</th>
      <th style="padding:6px 10px;text-align:right">Guests*</th>
      <th style="padding:6px 10px;text-align:right">Total</th></tr>
    </thead>
    <tbody>
      ${mealRow('Breakfast', tomorrowCounts.breakfast)}
      ${mealRow('Lunch',     tomorrowCounts.lunch)}
      ${mealRow('Dinner',    tomorrowCounts.dinner)}
    </tbody>
  </table>
  <div style="font-size:11px;color:#64748b;margin-top:4px">*Guests are also counted in Adults.</div>

  ${
    allergyRows
      ? `<h3 style="margin-top:18px;margin-bottom:6px">⚠ Allergies on tomorrow's roster</h3>
        <table style="border-collapse:collapse;width:100%;font-size:13px;border:1px solid #fecaca">
          <tbody>${allergyRows}</tbody>
        </table>`
      : `<div style="margin-top:18px;color:#64748b;font-size:13px">No allergies flagged for tomorrow's roster.</div>`
  }

  <h3 style="margin-top:18px;margin-bottom:6px">Roster</h3>
  ${
    personRows
      ? `<table style="border-collapse:collapse;width:100%;font-size:13px;border:1px solid #e5e7eb">
          <thead style="background:#f8fafc"><tr>
            <th style="padding:6px 10px;text-align:left">Name</th>
            <th style="padding:6px 10px;text-align:left">Member #</th>
            <th style="padding:6px 10px;text-align:left">Meals</th>
          </tr></thead>
          <tbody>${personRows}</tbody>
        </table>`
      : `<div style="color:#64748b;font-size:13px">No bookings yet for ${esc(tomorrowYmd)}.</div>`
  }
</body></html>`;
  };

  const sendTomorrowEmail = async () => {
    if (tomorrowSending) return;
    if (!tomorrowRows.length) {
      const ok = window.confirm(
        `No bookings yet for ${tomorrowYmd}. Send the email anyway (with a "no bookings" note)?`
      );
      if (!ok) return;
    }
    setTomorrowSending(true);
    setTomorrowMsg('Queuing…');

    const payload = {
      id: makeProfileId(),
      to: CHEF_EMAIL,
      kind: `attendance_${tomorrowYmd}`,
      subject: `Wauhillau — Attendance for ${tomorrowYmd}`,
      htmlBody: buildTomorrowEmailHtml(),
      status: 'pending',
      requestedAt: nowIso(),
      sentAt: '',
      error: '',
    };

    try {
      const res = await fetch(sheetUrl(OUTGOING_EMAILS_SHEET), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ data: [payload] }),
      });
      if (!res.ok) throw new Error(await res.text());
      setTomorrowMsg('Queued. The mailer will pick this up within ~5 minutes.');
      // best-effort: refresh last-sent in 6s
      setTimeout(fetchLastSent, 6000);
    } catch (e) {
      console.error('ChefDashboard: queue email failed', e);
      setTomorrowMsg('Failed to queue email.');
      alert('Failed to queue email.\n\n' + (e?.message || ''));
    } finally {
      setTomorrowSending(false);
    }
  };

  const [showTomorrowPreview, setShowTomorrowPreview] = useState(false);
  // The Tomorrow's Attendance panel is hidden by default — chef opens it with
  // the "Tomorrow's Attendance" button in the header. Stays hidden when the
  // chef switches the date selector to other days.
  const [showTomorrowPanel, setShowTomorrowPanel] = useState(false);

  // Counts: adult/child/guest per meal
  const counts = useMemo(() => {
    const init = {
      breakfast: { adults: 0, children: 0, guests: 0, total: 0 },
      lunch: { adults: 0, children: 0, guests: 0, total: 0 },
      dinner: { adults: 0, children: 0, guests: 0, total: 0 },
    };

    rows.forEach((r) => {
      const name = (r.name || '').toString();
      const isChild = /\(c\)/i.test(name);
      const isGuest = /\(g\)/i.test(name);

      ['breakfast', 'lunch', 'dinner'].forEach((meal) => {
        if ((r[meal] || '').toLowerCase() === 'yes') {
          if (isChild) {
            init[meal].children += 1;
          } else {
            init[meal].adults += 1;
            if (isGuest) init[meal].guests += 1;
          }
          init[meal].total += 1;
        }
      });
    });

    return init;
  }, [rows]);

  const dayTotals = useMemo(() => {
    const meals = Object.values(counts);
    const sum = (k) => meals.reduce((acc, m) => acc + m[k], 0);
    return {
      adults: sum('adults'),
      children: sum('children'),
      guests: sum('guests'),
      total: sum('total'),
    };
  }, [counts]);

  return (
    <div style={S.page}>
      {/* Header */}
      <div style={S.headerRow}>
        <div>
          <div style={S.hTitle}>Chef Dashboard</div>
          <div style={S.hSub}>Meals • Menu • Counts</div>
        </div>

        <div style={S.headerRight}>
          <button
            style={{
              ...S.themeBtn,
              background: showTomorrowPanel
                ? (theme === 'dark' ? '#0ea5e9' : '#0284c7')
                : S.themeBtn.background,
              color: showTomorrowPanel ? 'white' : S.themeBtn.color,
              borderColor: showTomorrowPanel
                ? (theme === 'dark' ? '#0ea5e9' : '#0284c7')
                : S.themeBtn.borderColor,
              fontWeight: 700,
            }}
            onClick={() => {
              setShowTomorrowPanel((s) => {
                const next = !s;
                if (next) {
                  // Refresh data when opening so the panel reflects latest bookings
                  fetchTomorrowRows();
                  refreshAllergyData();
                  fetchLastSent();
                }
                return next;
              });
            }}
          >
            📅 {showTomorrowPanel ? 'Hide' : 'Tomorrow'}
          </button>

          <button
            style={S.themeBtn}
            onClick={() => setTheme((t) => (t === 'dark' ? 'light' : 'dark'))}
          >
            {theme === 'dark' ? 'Light Mode' : 'Dark Mode'}
          </button>

          <button
            style={S.logoutBtn}
            onClick={() => {
              localStorage.removeItem('authenticatedUser');
              window.location.reload();
            }}
          >
            Logout
          </button>
        </div>
      </div>

      {/* Date + quick actions */}
      <div style={S.controlsRow}>
        <div style={S.controlGroup}>
          <label style={S.label}>Date</label>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            style={S.dateInput}
          />
        </div>

        <button
          style={S.refreshBtn}
          onClick={() => {
            fetchBookingsForDate(selectedDate);
            fetchMenuForDate(selectedDate);
          }}
        >
          Refresh
        </button>
      </div>

      {/* Counts */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)',
          gap: 12,
          marginTop: 12,
        }}
      >
        {['breakfast', 'lunch', 'dinner'].map((meal) => {
          const spec = specialLabelsFor(selectedDate);
          const label = spec.label[meal];
          const enabled = spec.enabled[meal];

          return (
            <div
              key={meal}
              style={{
                ...S.card,
                opacity: enabled ? 1 : 0.5,
              }}
            >
              <div style={S.cardTitle}>{label ? label : '(Not offered)'}</div>

              <div style={S.countRow}>
                Adults: <b>{counts[meal].adults}</b>
              </div>
              <div style={S.countRow}>
                Children: <b>{counts[meal].children}</b>
              </div>
              <div style={S.countRow}>
                Guests: <b>{counts[meal].guests}</b>
              </div>
              <div style={S.countRow}>
                Total: <b>{counts[meal].total}</b>
              </div>
            </div>
          );
        })}
      </div>

      {/* Day totals */}
      <div style={{ ...S.card, marginTop: 12 }}>
        <div style={S.cardTitle}>Day Totals</div>
        <div style={S.countRow}>
          Adults: <b>{dayTotals.adults}</b>
        </div>
        <div style={S.countRow}>
          Children: <b>{dayTotals.children}</b>
        </div>
        <div style={S.countRow}>
          Guests: <b>{dayTotals.guests}</b>
        </div>
        <div style={S.countRow}>
          Total: <b>{dayTotals.total}</b>
        </div>
      </div>

      {/* Menu Editor */}
      <div style={{ ...S.card, marginTop: 12 }}>
        <div style={S.menuHeader}>
          <div style={S.cardTitle}>Menu — {selectedDate}</div>
          <div style={S.muted}>{menuLoading ? 'Loading…' : menuMsg}</div>
        </div>

        <div style={S.menuGrid}>
          <div style={S.menuField}>
            <label style={S.label}>Breakfast</label>
            <textarea
              value={menu.breakfast}
              onChange={(e) => setMenu((m) => ({ ...m, breakfast: e.target.value }))}
              style={S.textarea}
              rows={2}
              placeholder="Breakfast menu…"
            />
          </div>

          <div style={S.menuField}>
            <label style={S.label}>Lunch</label>
            <textarea
              value={menu.lunch}
              onChange={(e) => setMenu((m) => ({ ...m, lunch: e.target.value }))}
              style={S.textarea}
              rows={2}
              placeholder="Lunch menu…"
            />
          </div>

          <div style={S.menuField}>
            <label style={S.label}>Dinner</label>
            <textarea
              value={menu.dinner}
              onChange={(e) => setMenu((m) => ({ ...m, dinner: e.target.value }))}
              style={S.textarea}
              rows={2}
              placeholder="Dinner menu…"
            />
          </div>

          <div style={S.menuField}>
            <label style={S.label}>Chef Notes</label>
            <textarea
              value={menu.chefs_note}
              onChange={(e) => setMenu((m) => ({ ...m, chefs_note: e.target.value }))}
              style={S.textarea}
              rows={2}
              placeholder="Notes for members…"
            />
          </div>
        </div>

        <div style={{ marginTop: 10 }}>
          <button style={S.primaryBtn} onClick={saveMenu} disabled={menuLoading}>
            {menuLoading ? 'Saving…' : 'Save Menu'}
          </button>
        </div>
      </div>

      {/* Tomorrow's Attendance — shown when chef opens it from the header */}
      {showTomorrowPanel && (
      <div style={{ ...S.card, marginTop: 12 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', flexWrap: 'wrap', gap: 10 }}>
          <div>
            <div style={S.cardTitle}>Tomorrow's Attendance — {tomorrowYmd}</div>
            <div style={S.muted}>
              {tomorrowLoading
                ? 'Loading…'
                : `${tomorrowRows.length} booking row${tomorrowRows.length === 1 ? '' : 's'} on file`}
              {lastSentAt ? ` · last sent ${lastSentAt}` : ' · not sent yet'}
            </div>
          </div>
          <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            <button
              onClick={() => { fetchTomorrowRows(); refreshAllergyData(); fetchLastSent(); }}
              style={S.themeBtn}
              disabled={tomorrowLoading || tomorrowSending}
            >
              Refresh
            </button>
            <button
              onClick={() => setShowTomorrowPreview((s) => !s)}
              style={S.themeBtn}
              disabled={tomorrowLoading}
            >
              {showTomorrowPreview ? 'Hide preview' : 'Preview email'}
            </button>
            <button
              onClick={sendTomorrowEmail}
              style={{ ...S.primaryBtn, opacity: tomorrowSending ? 0.6 : 1 }}
              disabled={tomorrowSending || tomorrowLoading}
            >
              {tomorrowSending ? 'Queuing…' : 'Send to chef now'}
            </button>
            <button
              onClick={() => setShowTomorrowPanel(false)}
              style={S.themeBtn}
              title="Close"
            >
              ✕ Close
            </button>
          </div>
        </div>

        {/* Counts grid */}
        <div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: isMobile ? '1fr' : 'repeat(3, 1fr)', gap: 8 }}>
          {['breakfast', 'lunch', 'dinner'].map((meal) => {
            const c = tomorrowCounts[meal];
            return (
              <div
                key={meal}
                style={{
                  border: `1px solid ${theme === 'dark' ? '#1f2937' : '#e5e7eb'}`,
                  borderRadius: 12,
                  padding: 10,
                  background: theme === 'dark' ? 'rgba(15,23,42,0.5)' : '#f8fafc',
                }}
              >
                <div style={{ fontSize: 12, opacity: 0.7, textTransform: 'uppercase', letterSpacing: 1 }}>
                  {meal}
                </div>
                <div style={{ fontSize: 22, fontWeight: 900, marginTop: 2 }}>{c.total}</div>
                <div style={{ fontSize: 12, opacity: 0.75 }}>
                  {c.adults} adults · {c.children} children · {c.guests} guests
                </div>
              </div>
            );
          })}
        </div>

        {/* Allergies surface */}
        {tomorrowAllergies.length > 0 && (
          <div style={{ marginTop: 12, padding: 10, borderRadius: 10, border: `1px solid ${theme === 'dark' ? 'rgba(239,68,68,0.45)' : '#fecaca'}`, background: theme === 'dark' ? 'rgba(239,68,68,0.10)' : 'rgba(254,202,202,0.35)' }}>
            <div style={{ fontWeight: 800, marginBottom: 6 }}>⚠ Allergies on tomorrow's roster</div>
            {tomorrowAllergies.map((a) => (
              <div key={a.rowId} style={{ fontSize: 13, marginTop: 2 }}>
                <span style={{ fontWeight: a.severity === 'severe' ? 900 : 700 }}>
                  {a.severity === 'severe' ? '🚨 ' : ''}
                  {a.name}
                </span>
                <span style={{ opacity: 0.7 }}> (#{a.memberNumber})</span> — {a.summary}
              </div>
            ))}
          </div>
        )}

        {tomorrowMsg && (
          <div style={{ marginTop: 8, fontSize: 12, opacity: 0.8 }}>{tomorrowMsg}</div>
        )}

        {showTomorrowPreview && (
          <div
            style={{
              marginTop: 10,
              padding: 0,
              border: `1px solid ${theme === 'dark' ? '#1f2937' : '#e5e7eb'}`,
              borderRadius: 10,
              overflow: 'hidden',
              background: '#ffffff',
              color: '#0f172a',
            }}
          >
            <iframe
              title="Tomorrow's email preview"
              srcDoc={buildTomorrowEmailHtml()}
              style={{ width: '100%', height: 480, border: 'none', background: 'white' }}
            />
          </div>
        )}
      </div>
      )}

      {/* Bookings */}
      <div style={{ ...S.card, marginTop: 12 }}>
        <div style={S.cardTitle}>Bookings — {selectedDate}</div>
        {loading ? (
          <div style={S.muted}>Loading…</div>
        ) : rows.length === 0 ? (
          <div style={S.muted}>No meals booked for this date.</div>
        ) : (
          rows.map((row) => {
            const rowSpec = specialLabelsFor(row.date);
            const allergy = resolveAllergyForRow(row, allergyData);
            const dark = theme === 'dark';
            const allergyPillStyles = allergy
              ? severityPillStyle(allergy.severity, dark)
              : null;
            const allergySummary = allergy
              ? summaryString(serializeChips(allergy.allergies), allergy.note)
              : '';

            return (
              <div key={row.id} style={S.bookingRow}>
                <div style={S.bookingTop}>
                  <div style={S.bookingTitle}>
                    {row.name}{' '}
                    <span style={S.muted}>({row.memberNumber || ''})</span>
                    {allergy && allergySummary ? (
                      <span
                        style={{
                          ...S.allergyPill,
                          borderColor: allergyPillStyles.borderColor,
                          background: allergyPillStyles.background,
                          color: allergyPillStyles.color,
                          fontWeight: allergyPillStyles.fontWeight,
                        }}
                        title={allergySummary}
                      >
                        {allergy.severity === 'severe' ? '🚨' : '⚠'} {allergySummary}
                      </span>
                    ) : null}
                    {row.member ? (
                      <span style={{ ...S.muted, marginLeft: 8 }}>Member: {row.member}</span>
                    ) : null}
                  </div>

                  <button style={S.deleteBtn} onClick={() => deleteRow(row)}>
                    Delete Row
                  </button>
                </div>

                <div style={S.checkboxRow}>
                  {['breakfast', 'lunch', 'dinner'].map((meal) => {
                    const labelText = rowSpec.label[meal];
                    const enabled = rowSpec.enabled[meal];
                    if (!labelText) return null;

                    const checked = (row[meal] || '').toLowerCase() === 'yes';

                    return (
                      <label
                        key={meal}
                        style={{
                          ...S.checkPill,
                          opacity: enabled ? 1 : 0.45,
                          cursor: enabled ? 'pointer' : 'not-allowed',
                        }}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          disabled={!enabled}
                          onChange={() => toggleMeal(row, meal)}
                          style={{ transform: 'scale(1.15)' }}
                        />
                        <span style={{ fontWeight: 800 }}>{labelText}</span>
                      </label>
                    );
                  })}
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

const stylesFor = (theme) => {
  const dark = theme === 'dark';

  return {
    page: {
      padding: 20,
      fontFamily: 'Arial',
      background: dark ? '#0f172a' : '#f8fafc',
      color: dark ? '#e5e7eb' : '#0f172a',
      minHeight: '100vh',
    },

    headerRow: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'flex-end',
      gap: 12,
      flexWrap: 'wrap',
    },
    headerRight: {
      display: 'flex',
      gap: 10,
      alignItems: 'center',
      flexWrap: 'wrap',
    },
    hTitle: {
      fontSize: 22,
      fontWeight: 900,
      letterSpacing: 0.2,
    },
    hSub: {
      fontSize: 12,
      opacity: 0.75,
      marginTop: 2,
    },

    muted: {
      color: dark ? '#94a3b8' : '#64748b',
      fontSize: 12,
    },

    allergyPill: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      marginLeft: 8,
      padding: '3px 10px',
      borderRadius: 999,
      border: dark ? '1px solid rgba(253, 224, 71, 0.35)' : '1px solid rgba(146, 64, 14, 0.25)',
      background: dark ? 'rgba(253, 224, 71, 0.10)' : 'rgba(251, 191, 36, 0.18)',
      color: dark ? '#fde68a' : '#92400e',
      fontSize: 12,
      fontWeight: 800,
      maxWidth: '100%',
    },

    controlsRow: {
      display: 'flex',
      gap: 10,
      alignItems: 'flex-end',
      flexWrap: 'wrap',
      marginTop: 12,
    },
    controlGroup: {
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
    },
    label: {
      fontSize: 12,
      opacity: 0.8,
      fontWeight: 700,
    },
    dateInput: {
      padding: '10px 12px',
      borderRadius: 12,
      border: dark ? '1px solid #334155' : '1px solid #cbd5e1',
      background: dark ? '#0b1220' : '#ffffff',
      color: dark ? '#e5e7eb' : '#0f172a',
      outline: 'none',
      minWidth: 160,
    },

    card: {
      border: dark ? '1px solid rgba(148,163,184,0.22)' : '1px solid #e2e8f0',
      borderRadius: 16,
      padding: 14,
      background: dark ? 'rgba(15,23,42,0.55)' : '#ffffff',
      boxShadow: dark ? '0 10px 30px rgba(0,0,0,0.25)' : '0 8px 22px rgba(15,23,42,0.06)',
    },
    cardTitle: {
      fontWeight: 900,
      marginBottom: 8,
    },
    countRow: {
      marginTop: 4,
      fontSize: 14,
    },

    menuHeader: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      gap: 10,
      flexWrap: 'wrap',
    },
    menuGrid: {
      display: 'grid',
      gridTemplateColumns: '1fr',
      gap: 10,
      marginTop: 10,
    },
    menuField: {
      display: 'flex',
      flexDirection: 'column',
      gap: 6,
    },
    textarea: {
      width: '100%',
      resize: 'vertical',
      borderRadius: 12,
      border: dark ? '1px solid #334155' : '1px solid #cbd5e1',
      background: dark ? '#0b1220' : '#ffffff',
      color: dark ? '#e5e7eb' : '#0f172a',
      padding: '10px 12px',
      outline: 'none',
      lineHeight: 1.3,
      fontSize: 14,
    },

    bookingRow: {
      borderTop: dark ? '1px dashed rgba(148,163,184,0.25)' : '1px dashed #e2e8f0',
      paddingTop: 12,
      marginTop: 12,
    },
    bookingTop: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'baseline',
      gap: 10,
      flexWrap: 'wrap',
    },
    bookingTitle: {
      fontWeight: 900,
      fontSize: 15,
      lineHeight: 1.25,
      wordBreak: 'break-word',
    },

    checkboxRow: {
      display: 'flex',
      gap: 10,
      flexWrap: 'wrap',
      marginTop: 10,
    },
    checkPill: {
      display: 'inline-flex',
      alignItems: 'center',
      gap: 8,
      padding: '10px 12px',
      borderRadius: 999,
      border: dark ? '1px solid #334155' : '1px solid #cbd5e1',
      background: dark ? 'rgba(2,6,23,0.35)' : '#f8fafc',
      userSelect: 'none',
    },

    primaryBtn: {
      padding: '10px 14px',
      borderRadius: 12,
      border: 'none',
      background: '#22c55e',
      color: '#fff',
      fontWeight: 900,
      cursor: 'pointer',
    },
    refreshBtn: {
      padding: '10px 14px',
      borderRadius: 12,
      border: dark ? '1px solid #334155' : '1px solid #cbd5e1',
      background: dark ? 'rgba(2,6,23,0.35)' : '#ffffff',
      color: dark ? '#e5e7eb' : '#0f172a',
      fontWeight: 900,
      cursor: 'pointer',
    },
    deleteBtn: {
      padding: '8px 12px',
      borderRadius: 12,
      border: dark ? '1px solid rgba(248,113,113,0.35)' : '1px solid rgba(248,113,113,0.35)',
      background: dark ? 'rgba(248,113,113,0.10)' : 'rgba(248,113,113,0.10)',
      color: dark ? '#fecaca' : '#991b1b',
      fontWeight: 900,
      cursor: 'pointer',
    },
    themeBtn: {
      padding: '10px 14px',
      borderRadius: 12,
      border: dark ? '1px solid #334155' : '1px solid #cbd5e1',
      background: dark ? 'rgba(2,6,23,0.35)' : '#ffffff',
      color: dark ? '#e5e7eb' : '#0f172a',
      fontWeight: 900,
      cursor: 'pointer',
    },
    logoutBtn: {
      padding: '10px 14px',
      borderRadius: 12,
      border: dark ? '1px solid rgba(148,163,184,0.25)' : '1px solid #cbd5e1',
      background: dark ? 'rgba(2,6,23,0.35)' : '#ffffff',
      color: dark ? '#e5e7eb' : '#0f172a',
      fontWeight: 900,
      cursor: 'pointer',
    },
  };
};
